import { trackById, trackByIndex } from 'src/app/shared/utils/track-by.util';
import { ToastService } from 'src/app/services/toast.service';
import { Observable, catchError, map, of, timeout } from 'rxjs';
import { IGridService } from '../grid/grid.service';
import { applyFieldConfigDefaults } from './field-config-defaults.helper';
import { FormFieldType } from './formFieldType.enum';
import { FormField } from './formField.model';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ICrudService } from '../grid/crud.service';
import { ChangeDetectorRef, Inject, Injector } from '@angular/core';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import { TranslateService } from 'src/app/services/translate.service';
import { ISearchFieldData } from '../grid/baseGrid.component';
import { ColumnDetails } from 'src/app/models/columnDetails.model';

export interface IFormDialogData {
    id?: string;
    isReadonly?: boolean;
    formDeletes?: boolean;
    deleteService?: any;
    columnDetails?: { [property: string]: ColumnDetails }
}

export interface IOrganizationLevelEntity {
  organizationLevelId: string;
}

const FIELD_CONFIGURATION_TIMEOUT_MS = 5000;

function hasFieldConfiguration<T>(service: ICrudService<T>): service is ICrudService<T> & Pick<IGridService<T>, 'getFieldConfiguration'> {
    return typeof (service as Partial<IGridService<T>>).getFieldConfiguration === 'function';
}

export abstract class FormComponent<T> {
    isFormLoading = false;
    isLoading = false;
    entity: T;
    entityId: string;
    fields: FormField[];
    cachedDisabled: boolean = true;
    isAdd: boolean;
    FormFieldType = FormFieldType;
    isReadonly: boolean = false;
    formDeletes: boolean = false;
    mainButtonText: string = '';
    closeButtonText: string = '';
    title: string;
    timeError: string;
    protected translateService: TranslateService;
    protected toastService: ToastService;
    protected searchMatDialog: MatDialog;
    protected dateFormatService: DateFormatService;
    protected changeDetectorRef: ChangeDetectorRef;
    columnDetails: { [property: string]: ColumnDetails; } = null;

    constructor(public dialogRef: MatDialogRef<FormComponent<T>>,
        protected service: ICrudService<T>,
        @Inject(MAT_DIALOG_DATA) public dialogData: IFormDialogData,
        title: string,
        protected injector: Injector
    ) {
        this.translateService = this.injector.get(TranslateService);
        this.toastService = this.injector.get(ToastService);
        this.searchMatDialog = this.injector.get(MatDialog);
        this.dateFormatService = this.injector.get(DateFormatService);
        this.changeDetectorRef = this.injector.get(ChangeDetectorRef);
        this.entityId = dialogData?.id;
        this.isAdd = !this.entityId;
        this.isReadonly = dialogData?.isReadonly;
        this.formDeletes = dialogData?.formDeletes;
        this.columnDetails = dialogData?.columnDetails;
        this.setTitle(title);
        this.setMainButtonText();
        this.closeButtonText = this.translateService.getDynamicTranslation(this.isReadonly ? 'Close' : 'Cancel');
        this.setMainButtonClick();

        let _fields: FormField[];
        Object.defineProperty(this, 'fields', {
            get: () => _fields,
            set: (v: FormField[]) => {
                _fields = v;
                this.updateFieldVisibility();
            },
            configurable: true,
            enumerable: true
        });
    }

    abstract getNewObject(): T;

    setTitle(title: string, addTitlePrefix: boolean = true) {
        let action: string;
        switch (true) {
            case this.formDeletes:
                action = 'Delete';
                break;
            case this.isReadonly:
                action = 'Manage';
                break;
            case this.isAdd:
                action = 'Create';
                break;
            default:
                action = 'Edit';
        }
        this.title = addTitlePrefix ? `${this.translateService.getDynamicTranslation(action)} ${this.translateService.getDynamicTranslation(title)}` : title;
    }

    getEntity(): Observable<T> {
        return this.isAdd ? this.getNewObjectWithDefaults() : this.service.getById(this.entityId);
    }

    private getNewObjectWithDefaults(): Observable<T> {
        return this.loadDefaultsConfig().pipe(map(config => {
            const newObject = this.getNewObject();
            applyFieldConfigDefaults(newObject, config);
            return newObject;
        }));
    }

    private loadDefaultsConfig(): Observable<{ [property: string]: ColumnDetails } | null> {
        if (!hasFieldConfiguration(this.service)) return of(null);
        return this.service.getFieldConfiguration().pipe(
            timeout(FIELD_CONFIGURATION_TIMEOUT_MS),
            catchError(error => {
                this.toastService.showError(error);
                return of(null);
            })
        );
    }

    isDisabled(): boolean {
        if (this.formDeletes)
            return false;

        if (!this.fields)
            return true;

        let disabled = false;
        this.fields.forEach(field => {
            const value = this.entity[field.property];

            if (field.required && !field.readonly && (value === undefined || value === null || (Array.isArray(value) && value.length === 0))) {
                disabled = true;
            }

            // DASH-2676: block submission while any visible field fails its opt-in custom validator.
            if (field.visible && !field.readonly && field.validator && field.validator(value, this.entity)) {
                disabled = true;
            }

            if (field.type === FormFieldType.Array) {
                if (!value || value.length === 0 || field.readonly || !field.required) {
                    return;
                }
                field.items.forEach(arrayItem => {
                    arrayItem.forEach(item => {
                        if (item.required && !item.readonly &&
                            value.some(row => row[item.property] === undefined || row[item.property] === null)
                        ) {
                            disabled = true;
                        }
                    });
                });
            }
        });

        return disabled;
    }

    updateFieldVisibility(): void {
        if (!this.fields || !this.entity) return;
        this.fields.forEach(field => {
            field.visible = field.isVisible(this.entity);
            field.additionalLabelText = field.additionalLabel(this.entity);
            if (field.type === FormFieldType.Array && Array.isArray(this.entity?.[field.property as keyof T])) {
                const rows = this.entity[field.property as keyof T] as unknown[];
                field.items.forEach(arrayRow => {
                    arrayRow.forEach(fieldItem => {
                        fieldItem.visible = fieldItem.isVisible(this.entity);
                        fieldItem.rowVisibility = rows.map(row => fieldItem.isVisible(row));
                        fieldItem.additionalLabelText = fieldItem.additionalLabel(this.entity);
                        fieldItem.isRequired = field.required && fieldItem.required;
                        fieldItem.itemCssClass = fieldItem.cssClass === 'multiple-row' ? `${fieldItem.columnSpanClass} p-0` : '';
                    });
                });
            }
        });
        this.cachedDisabled = this.isDisabled();
    }

    // DASH-2676: run a field's opt-in custom validator against the current entity value. Returns the inline
    // error message to display, or null when the field is valid or has no validator.
    getFieldError(field: FormField): string | null {
        if (!field?.validator || !field.visible || field.readonly) return null;
        return field.validator(this.entity?.[field.property as keyof T], this.entity) ?? null;
    }

    // DASH-2676: recompute the Save button's disabled state when a field loses focus. Inline validation errors
    // surface via Angular Material's own touched state, so no custom touched flag is needed here.
    onFieldBlur(_field: FormField): void {
        this.updateFieldVisibility();
    }

    save() {
        if (!this.validate())
            return;

        const model = JSON.parse(JSON.stringify(this.entity));
        this.onBeforeSave(model);
        this.isLoading = true;

        this.saveAction(model)
            .subscribe({
                next: (_) => this.afterSaveAction(_),
                error: this.handleError
            });
    }

    afterSaveAction(response) {
        this.isLoading = false;
        this.toastService.showSuccess('Success', true);
        this.closeDialog(response);
    }

    closeDialog(data: Object = null) {
        this.dialogRef.close(true);
    }

    saveAction(model) {
        return this.isAdd ? this.service.create(model) : this.service.update(model);;
    }

    delete() {
        this.isLoading = true;
        this.service.delete(this.entityId)
            .subscribe({
                next: () => {
                    this.isLoading = false;
                    this.toastService.showSuccess('Success', true);
                    this.dialogRef.close(true);
                },
                error: this.handleError
            });
    }

    handleError = (error: unknown): void => {
        this.isLoading = false;
        this.toastService.showError(error);
    }

    abstract validate(): boolean;

    onBeforeSave(model: T) {
    }

    getCssClass(fieldItem: FormField): string {
        return fieldItem.cssClass === 'multiple-row' ? `${fieldItem.columnSpanClass} p-0` : '';
    }

    close() {
        this.dialogRef.close(false);
    }

    mainButtonClick = () => { this.formDeletes ? this.delete() : this.save() }

    isFieldRequired(field: FormField, fieldItem: FormField): boolean {
        return field.required && fieldItem.required;
    }

    onKeyPressValidateInput(event: KeyboardEvent, displayFormat: RegExp, min?: number): void {
        if (!displayFormat.test(event.key)) {
            event.preventDefault();
            return;
        }

        if (event.key === '-') {
            // Block minus if field has min >= 0 (negative values not allowed)
            if (min != null && min >= 0) {
                event.preventDefault();
                return;
            }
            // Only allow minus at the start and prevent duplicates
            const input = event.target as HTMLInputElement;
            const selStart = input.selectionStart ?? 0;
            const selEnd = input.selectionEnd ?? 0;
            const selectionContainsMinus = input.value.slice(selStart, selEnd).includes('-');
            if (selStart !== 0 || (input.value.includes('-') && !selectionContainsMinus)) {
                event.preventDefault();
            }
        }
    }

    onTimeChange(event: string, field: FormField, fieldItem: FormField = undefined, index = -1) {
        this.dateFormatService.applyTimeChange(this.entity, field, event, fieldItem, index);
        this.updateFieldVisibility();
    }

    openSearchDialog(field: FormField) {
        const data: ISearchFieldData = {
            ...field?.additonalSearchData,
            selected: this.entity[field.property],
            isMultiple: field.type === FormFieldType.MultipleSelection,
        }

        const dialogRef = this.searchMatDialog.open(field.searchComponent, {
            width: '960px',
            data: data,
            panelClass: 'resizable-dialog',
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (!result) return;
            const { value, items } = result;
            if (!(typeof value === 'boolean')) {
                if (data.isMultiple) {
                    (items || []).forEach(item => {
                        const exists = field.options.some(opt => opt.id === item.id);
                        if (!exists) field.options.push(item);
                    });
                } else if (items) {
                    const exists = field.options.some(opt => opt.id === items.id);
                    if (!exists) field.options.push(items);
                }
                this.entity[field.property] = typeof value === 'object' ? [...value] : value;
                field.onChange({ value: value }, field);
                this.updateFieldVisibility();
            }
        })
    }

    setColumnDetails() {
        if (this.columnDetails) {
            this.fields.forEach(field => {
                if (this.columnDetails.hasOwnProperty(field.property) && this.columnDetails[field.property]?.tooltip) {
                    field.tooltip = this.columnDetails[field.property].tooltip;
                }
            })
        }
    }

    clearSelect(field: FormField) {
        this.entity[field.property as keyof T] = null;
        field?.onChange?.({ value: null }, field);
        this.updateFieldVisibility();
    }

    clearArraySelect(field: FormField, fieldItem: FormField, index: number) {
        (this.entity[field.property as keyof T] as unknown[])[index][fieldItem.property] = null;
        fieldItem?.onChange?.({ value: null }, field, index);
        this.updateFieldVisibility();
    }

    setMainButtonText(text: string = this.formDeletes ? 'Delete' : 'Save'): void {
        const translatedText = this.translateService.getDynamicTranslation(text);
        this.mainButtonText = translatedText;
    }

    setMainButtonClick(clickHandler: () => void = this.mainButtonClick): void {
        this.mainButtonClick = clickHandler;
    }

    public trackByIndex = trackByIndex;
    public trackByEntityId = trackById;
}

